// CR121: Hook for managing persisted Auto Mode configuration per batch run
// CR131: Also persists changes to ops_settings for future runs
import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";
import {
  AutoModeConfig,
  StepGroupKey,
  S1SubstepKey,
  DEFAULT_AUTO_MODE_CONFIG,
  getAutoModeConfig,
  getNextStepGroup,
  getNextS1Substep,
  STEP_GROUP_SEQUENCE,
  S1_SUBSTEP_SEQUENCE,
} from "@/lib/batch-runs/autoModeTypes";
import { resolveFlyerCaptureMode } from "@/lib/batch-runs/flyerCaptureMode";
import {
  getStepBlockedReason as getStepBlockedReasonUtil,
  type GatingContext
} from "@/lib/batch-runs/autoModeGating";
import type { SnapshotStabilityPhase } from "@/lib/batch-runs/snapshotStability";
import { shouldBlockS4ForSnapshotSettling } from "@/lib/batch-runs/s1StepState";

type StepStatus =
  | "NOT_STARTED"
  | "PENDING"
  | "RUNNING"
  | "COMPLETE"
  | "COMPLETE_WITH_TAIL"
  | "PARTIAL"
  | "FAILED"
  | "SKIPPED"
  | "NEEDS_ACTION";

interface BatchRunStep {
  step_key: string;
  status: string;
  records_out: number;
  started_at?: string | null;
}

interface UseBatchRunAutoModeProps {
  batchRunId: string;
  currentConfigJson: Record<string, unknown> | null | undefined;
  /** Source-of-truth enabled sources from batch run row. */
  sourcesEnabled: string[];
  steps: BatchRunStep[];
  /** Callback when a step should be triggered */
  onTriggerStep: (stepKey: string, options?: { mode?: string }) => Promise<void>;
  /** Callback when config changes (refresh data) */
  onConfigChanged: () => void;
  /** Current S1 substep status getter */
  getS1SubstepStatus?: (substepKey: S1SubstepKey) => StepStatus;
  /** Optional flyer-specific stats for zero-output detection */
  flyerStats?: {
    dealsExtracted: number;
    candidatesFound: number;
  };
  /** Optional ordered step-group sequence override (e.g. DOWNLOAD_ONLY excludes S1). */
  stepGroupSequence?: StepGroupKey[];
  /** Optional phase-1 groups override for top-level auto toggles and fallback checks. */
  phase1Groups?: StepGroupKey[];
  /** Canonical S1 completion state from strict gate evaluator. */
  s1StateSnapshot?: {
    isComplete: boolean;
    blockedReasons: string[];
    capture?: { terminal?: boolean; scope_frozen?: boolean };
    ocr?: { terminal?: boolean };
    signals?: { terminal?: boolean; ready?: boolean };
    ai?: { terminal?: boolean };
    locate_crop?: { terminal?: boolean };
  } | null;
  s1SnapshotStabilityPhase?: SnapshotStabilityPhase | null;
}

interface AutoModeOrchestratorState {
  /** Active orchestration running */
  isOrchestrating: boolean;
  /** Current step group being processed */
  currentGroup: StepGroupKey | null;
  /** Current S1 substep being processed */
  currentSubstep: S1SubstepKey | null;
  /** Error message if orchestration stopped */
  errorMessage: string | null;
}

const PHASE1_GROUPS: StepGroupKey[] = ["S0.5", "S1", "S2", "S4", "S5"];

export function useBatchRunAutoMode({
  batchRunId,
  currentConfigJson,
  sourcesEnabled,
  steps,
  onTriggerStep,
  onConfigChanged,
  getS1SubstepStatus,
  flyerStats,
  stepGroupSequence = STEP_GROUP_SEQUENCE,
  phase1Groups = PHASE1_GROUPS,
  s1StateSnapshot,
  s1SnapshotStabilityPhase,
}: UseBatchRunAutoModeProps) {
  const { toast } = useToast();
  const [config, setConfig] = useState<AutoModeConfig>(() =>
    getAutoModeConfig(currentConfigJson)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [orchestratorState, setOrchestratorState] = useState<AutoModeOrchestratorState>({
    isOrchestrating: false,
    currentGroup: null,
    currentSubstep: null,
    errorMessage: null,
  });

  // CR708: Track active reconciliation calls to avoid overlaps
  const activeReconciliationsRef = useRef<Set<string>>(new Set());

  // Ref to track latest state for async callbacks
  const configRef = useRef(config);
  configRef.current = config;

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const orchestratorRef = useRef(orchestratorState);
  orchestratorRef.current = orchestratorState;
  const flyerCaptureMode = resolveFlyerCaptureMode(currentConfigJson);

  // Update config when props change
  useEffect(() => {
    const newConfig = getAutoModeConfig(currentConfigJson);
    setConfig(newConfig);

    // Restore orchestrator state if it was running
    const persistedGroup = newConfig.batch_auto_run_state?.active
      ? newConfig.batch_auto_run_state.current_step_key
      : null;
    const fallbackGroup = newConfig.auto_state?.running_group;
    const restoredGroup = persistedGroup ?? fallbackGroup;
    if (restoredGroup) {
      setOrchestratorState(prev => ({
        ...prev,
        isOrchestrating: true,
        currentGroup: restoredGroup || null,
        currentSubstep: newConfig.auto_state?.running_substep || null,
      }));
    }
  }, [currentConfigJson]);

  /**
   * Persist config to database
   */
  const saveConfig = useCallback(async (newConfig: AutoModeConfig) => {
    setIsSaving(true);
    try {
      // Merge with existing config_json
      const existingConfig = currentConfigJson || {};
      const updatedConfig = {
        ...existingConfig,
        auto_mode_config: {
          ...newConfig,
        },
      };

      const { error } = await supabase
        .from("cr075_batch_runs")
        .update({ config_json: updatedConfig as unknown as Json })
        .eq("id", batchRunId);

      if (error) throw error;
      setConfig(newConfig);
      onConfigChanged();
    } catch (err) {
      console.error("[useBatchRunAutoMode] Failed to save config:", err);
      toast({
        title: "Error",
        description: "Failed to save auto mode settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [batchRunId, currentConfigJson, onConfigChanged, toast]);

  /**
   * Toggle auto mode for a specific step group
   * CR131: Also persists to ops_settings for future runs
   */
  const toggleAutoMode = useCallback(async (groupKey: StepGroupKey, enabled: boolean) => {
    const newConfig: AutoModeConfig = {
      ...configRef.current,
      auto_mode: {
        ...configRef.current.auto_mode,
        [groupKey]: enabled,
      },
    };
    await saveConfig(newConfig);

    // CR131: Also update ops_settings for future runs
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

        // First fetch current ops_settings
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const getResponse = await fetch(
          `${supabaseUrl}/functions/v1/ops-settings?key=batch.defaults.execution&scope=global`,
          {
            method: "GET",
            headers: { "apikey": anonKey, "Content-Type": "application/json" },
          }
        );

        if (getResponse.ok) {
          const currentSettings = await getResponse.json();
          const currentValue = currentSettings.value || {};

          // Map step group to ops_settings field
          let opsField: string | null = null;
          if (groupKey === "S0.5" || groupKey === "S1" || groupKey === "S2") {
            opsField = "auto_collection";
          } else if (groupKey === "S4" || groupKey === "S5") {
            opsField = "auto_processing";
          } else if (groupKey === "S6" || groupKey === "S7") {
            opsField = "auto_publish";
          }

          if (opsField) {
            const updatedValue = { ...currentValue, [opsField]: enabled };

            await fetch(`${supabaseUrl}/functions/v1/ops-settings`, {
              method: "PUT",
              headers: {
                "Authorization": `Bearer ${session.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                key: "batch.defaults.execution",
                scope: "global",
                value_json: updatedValue,
              }),
            });
          }
        }
      }
    } catch (opsErr) {
      // Non-critical: log but don't fail the toggle
      console.warn("[useBatchRunAutoMode] Failed to update ops_settings:", opsErr);
    }

    toast({
      title: enabled ? "Auto Mode Enabled" : "Auto Mode Disabled",
      description: `${groupKey} will ${enabled ? "automatically" : "manually"} execute. Saved as default for future runs.`,
    });
  }, [saveConfig, toast]);

  /**
   * Set phase-1 auto mode (S1/S2/S4/S5) in one atomic save.
   */
  const setPhase1AutoMode = useCallback(async (enabled: boolean) => {
    const nextAutoMode = { ...configRef.current.auto_mode };
    for (const group of phase1Groups) {
      nextAutoMode[group] = enabled;
    }

    const newConfig: AutoModeConfig = {
      ...configRef.current,
      batch_auto_run_enabled: enabled,
      auto_mode: nextAutoMode,
      auto_state: {
        running_group: enabled ? configRef.current.auto_state?.running_group || null : null,
        running_substep: enabled ? configRef.current.auto_state?.running_substep || null : null,
        last_triggered_at: configRef.current.auto_state?.last_triggered_at || (enabled ? new Date().toISOString() : null),
        last_error: null,
      },
      batch_auto_run_state: {
        active: enabled ? Boolean(configRef.current.batch_auto_run_state?.active) : false,
        current_step_key: enabled ? (configRef.current.batch_auto_run_state?.current_step_key || null) : null,
        last_step_key: configRef.current.batch_auto_run_state?.last_step_key || null,
        last_error: null,
        started_at: enabled
          ? (configRef.current.batch_auto_run_state?.started_at || new Date().toISOString())
          : null,
      },
    };

    await saveConfig(newConfig);
    toast({
      title: enabled ? "Batch Auto-Run Enabled" : "Batch Auto-Run Disabled",
      description: enabled
        ? "S1 → S2 → S4 → S5 will auto-run in sequence."
        : "Phase-1 step chaining is disabled.",
    });
  }, [saveConfig, toast, phase1Groups]);

  /**
   * Get the status of a step group
   */
  const getStepGroupStatus = useCallback((groupKey: StepGroupKey): StepStatus => {
    // For S1, we need to check all substeps
    if (groupKey === "S1") {
      if (!getS1SubstepStatus) {
        // Fall back to S1 step in the steps array
        const s1Step = stepsRef.current.find(s => s.step_key === "S1");
        return (s1Step?.status as StepStatus) || "NOT_STARTED";
      }

      // Check all S1 substeps
      const substepStatuses = S1_SUBSTEP_SEQUENCE.map(key => getS1SubstepStatus(key));
      const s14Status = getS1SubstepStatus("S1_4");
      const s15Status = getS1SubstepStatus("S1_5");

      // If any is running, group is running
      if (substepStatuses.some(s => s === "RUNNING")) return "RUNNING";

      // Preserve explicit terminal-tail semantics from S1.4 before generic completion promotion.
      if (s14Status === "COMPLETE_WITH_TAIL" || s15Status === "COMPLETE_WITH_TAIL") {
        return "COMPLETE_WITH_TAIL";
      }

      // Canonical strict gate (preferred): S1_COMPLETE only when evaluator says complete.
      if (s1StateSnapshot?.isComplete) return "COMPLETE";

      // S1_5 is compat-only. Treat S1 as complete at S1_4 terminal (or S1_5 terminal if present).
      if (s14Status === "COMPLETE" || s15Status === "COMPLETE") return "COMPLETE";

      // If any failed, group failed
      if (substepStatuses.some(s => s === "FAILED")) return "FAILED";

      // If any is partial, group is partial
      if (substepStatuses.some(s => s === "PARTIAL")) return "PARTIAL";

      // Otherwise pending
      return "PENDING";
    }

    // For other groups, just check the step
    const step = stepsRef.current.find(s => s.step_key === groupKey);
    return (step?.status as StepStatus) || "NOT_STARTED";
  }, [getS1SubstepStatus, s1StateSnapshot]);

  /**
   * CR175: Get the blocked reason for a step group (null if not blocked)
   */
  const getStepBlockedReason = useCallback((groupKey: StepGroupKey, options?: { strict?: boolean }): string | null => {
    const context: GatingContext = {
      getStepGroupStatus,
      sourcesEnabled,
      requireS05ForFlyerS1: flyerCaptureMode !== "IMAGE_CAPTURE",
      s1StateSnapshot,
      s1SnapshotStabilityPhase,
      getS1SubstepStatus,
      shouldBlockS4ForSnapshotSettling,
    };
    return getStepBlockedReasonUtil(groupKey, context, options);
  }, [getStepGroupStatus, sourcesEnabled, flyerCaptureMode, s1StateSnapshot, s1SnapshotStabilityPhase, getS1SubstepStatus]);

  /**
   * CR175: Check if a step group can be run

    if (groupKey === "S5") {
      const s4Status = getStepGroupStatus("S4");
      if (s4Status !== "COMPLETE") {
        return `Waiting for S4 (Normalize) to complete (status: ${s4Status})`;
      }
    }

    return null;
  }, [getStepGroupStatus, getS1SubstepStatus, s1StateSnapshot, s1SnapshotStabilityPhase, sourcesEnabled]);

  /**
   * Check if a step group can be run
   */
  const canRunStepGroup = useCallback((groupKey: StepGroupKey, options?: { strict?: boolean }): boolean => {
    // CR175: Use the blocked reason check - if blocked, can't run.
    return getStepBlockedReason(groupKey, options) === null;
  }, [getStepBlockedReason]);

  const persistBatchAutoRunState = useCallback(async (
    next: Partial<NonNullable<AutoModeConfig["batch_auto_run_state"]>>
  ) => {
    const current = configRef.current;
    const merged: AutoModeConfig = {
      ...current,
      batch_auto_run_enabled: current.batch_auto_run_enabled ?? true,
      auto_state: {
        running_group: next.current_step_key ?? current.auto_state?.running_group ?? null,
        running_substep: current.auto_state?.running_substep ?? null,
        last_triggered_at: current.auto_state?.last_triggered_at ?? null,
        last_error: next.last_error ?? current.auto_state?.last_error ?? null,
      },
      batch_auto_run_state: {
        active: next.active ?? current.batch_auto_run_state?.active ?? false,
        current_step_key: next.current_step_key ?? current.batch_auto_run_state?.current_step_key ?? null,
        last_step_key: next.last_step_key ?? current.batch_auto_run_state?.last_step_key ?? null,
        last_error: next.last_error ?? current.batch_auto_run_state?.last_error ?? null,
        started_at: next.started_at ?? current.batch_auto_run_state?.started_at ?? null,
      },
    };
    await saveConfig(merged);
  }, [saveConfig]);

  /**
   * Stop orchestration
   */
  const stopOrchestration = useCallback(async (reason: string) => {
    setOrchestratorState({
      isOrchestrating: false,
      currentGroup: null,
      currentSubstep: null,
      errorMessage: reason,
    });

    // Clear running state in config
    const newConfig: AutoModeConfig = {
      ...configRef.current,
      auto_state: {
        running_group: null,
        running_substep: null,
        last_triggered_at: configRef.current.auto_state?.last_triggered_at || null,
        last_error: reason,
      },
      batch_auto_run_state: {
        active: false,
        current_step_key: null,
        last_step_key: configRef.current.batch_auto_run_state?.last_step_key || null,
        last_error: reason,
        started_at: configRef.current.batch_auto_run_state?.started_at || null,
      },
    };
    await saveConfig(newConfig);

    toast({
      title: "Batch Auto-Run Stopped",
      description: reason,
      variant: reason.includes("Error") || reason.includes("Failed") ? "destructive" : "default",
    });
  }, [saveConfig, toast]);

  /**
   * Process orchestration tick - check if next step should be triggered
   */
  const processOrchestrationTick = useCallback(async () => {
    if (!orchestratorRef.current.isOrchestrating) return;

    const currentGroup = orchestratorRef.current.currentGroup;
    if (!currentGroup) return;

    const currentStatus = getStepGroupStatus(currentGroup);

    // Still running, check for staleness
    if (currentStatus === "RUNNING") {
      const stepData = stepsRef.current.find(s => s.step_key === currentGroup);
      if (stepData?.started_at && !activeReconciliationsRef.current.has(currentGroup)) {
        const startedAt = new Date(stepData.started_at).getTime();
        const now = Date.now();
        const STALE_THRESHOLD_MS = 5 * 60 * 1000;

        if (now - startedAt > STALE_THRESHOLD_MS) {
          console.warn(`[Orchestrator] Detected stale RUNNING step: ${currentGroup}. Triggering reconciliation.`);
          activeReconciliationsRef.current.add(currentGroup);
          
          toast({
            title: "Stale Step Detected",
            description: `Reconciling ${currentGroup} after 5m of no apparent progress...`,
          });

          try {
            await onTriggerStep(currentGroup, { mode: "RECONCILE" });
          } catch (err) {
            console.error(`[Orchestrator] Reconciliation trigger failed for ${currentGroup}:`, err);
          } finally {
            // Note: We don't remove from activeReconciliationsRef immediately 
            // since it might take a moment for the status to change in the next poll.
            setTimeout(() => {
              activeReconciliationsRef.current.delete(currentGroup);
            }, 10000);
          }
        }
      }
      return;
    }

    // Failed - stop orchestration
    if (currentStatus === "FAILED" && configRef.current.auto_policy.stop_on_failure) {
      await stopOrchestration(`${currentGroup} failed`);
      return;
    }

    // Partial - stop orchestration (terminal-tail semantics are not failures)
    if (currentStatus === "PARTIAL" && configRef.current.auto_policy.stop_on_failure) {
      await stopOrchestration(`${currentGroup} completed with partial results`);
      return;
    }

    // Complete / terminal-tail - check for next step
    if (currentStatus === "COMPLETE" || currentStatus === "COMPLETE_WITH_TAIL") {
      if (
        currentGroup === "S1" &&
        shouldBlockS4ForSnapshotSettling(
          s1SnapshotStabilityPhase,
          s1StateSnapshot?.isComplete ?? null,
        )
      ) {
        return;
      }

      if (currentGroup === "S1" && (!s1StateSnapshot || !s1StateSnapshot.isComplete)) {
        // Strict gate wait: do not advance to S4 until canonical S1 completion is true.
        return;
      }

      // Check for zero output in S1 AI step
      if (currentGroup === "S1" && flyerStats?.dealsExtracted === 0) {
        await stopOrchestration("S1 completed with zero deals extracted");
        return;
      }

      // Find next group
      const nextGroup = getNextStepGroup(currentGroup, stepGroupSequence);

      if (!nextGroup) {
        // All done!
        setOrchestratorState({
          isOrchestrating: false,
          currentGroup: null,
          currentSubstep: null,
          errorMessage: null,
        });
        await persistBatchAutoRunState({
          active: false,
          current_step_key: null,
          last_step_key: currentGroup,
          last_error: null,
        });
        toast({
          title: "Batch Auto-Run Complete",
          description: "All enabled step groups have completed",
        });
        return;
      }

      // Check if next group has auto mode enabled
      if (configRef.current.auto_mode[nextGroup]) {
        if (canRunStepGroup(nextGroup, { strict: true })) {
          // Trigger next group
          setOrchestratorState(prev => ({
            ...prev,
            currentGroup: nextGroup,
            currentSubstep: nextGroup === "S1" ? "S1_COLLECT" : null,
          }));
          await persistBatchAutoRunState({
            active: true,
            current_step_key: nextGroup,
            last_step_key: currentGroup,
            last_error: null,
            started_at: configRef.current.batch_auto_run_state?.started_at || new Date().toISOString(),
          });

          const stepToTrigger = nextGroup;
          try {
            await onTriggerStep(stepToTrigger);
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err ?? "Unknown trigger error");
            await stopOrchestration(`Error triggering ${nextGroup}: ${message}`);
            return;
          }
        } else {
          await stopOrchestration(`Cannot run ${nextGroup} - dependencies not met`);
        }
      } else {
        // Next group not auto-enabled, stop
        setOrchestratorState({
          isOrchestrating: false,
          currentGroup: null,
          currentSubstep: null,
          errorMessage: null,
        });
        await persistBatchAutoRunState({
          active: false,
          current_step_key: null,
          last_step_key: currentGroup,
          last_error: null,
        });
      }
    }
  }, [getStepGroupStatus, canRunStepGroup, onTriggerStep, stopOrchestration, flyerStats, toast, stepGroupSequence, s1StateSnapshot, s1SnapshotStabilityPhase, persistBatchAutoRunState]);

  /**
   * Start orchestration from a specific group
   */
  const startOrchestration = useCallback(async (startGroup: StepGroupKey) => {
    if (orchestratorRef.current.isOrchestrating) {
      toast({
        title: "Already Running",
        description: "Batch Auto-Run is already in progress",
        variant: "destructive",
      });
      return;
    }

    if (!canRunStepGroup(startGroup, { strict: true })) {
      const blockedReason = getStepBlockedReason(startGroup, { strict: true }) || `${startGroup} dependencies not met`;
      toast({
        title: "Cannot Start",
        description: blockedReason,
        variant: "destructive",
      });
      return;
    }

    // Update orchestrator state
    setOrchestratorState({
      isOrchestrating: true,
      currentGroup: startGroup,
      currentSubstep: startGroup === "S1" ? "S1_COLLECT" : null,
      errorMessage: null,
    });

    // Persist running state locally only
    const newConfig: AutoModeConfig = {
      ...configRef.current,
      batch_auto_run_enabled: configRef.current.batch_auto_run_enabled ?? true,
      auto_state: {
        running_group: startGroup,
        running_substep: startGroup === "S1" ? "S1_COLLECT" : null,
        last_triggered_at: new Date().toISOString(),
        last_error: null,
      },
      batch_auto_run_state: {
        active: true,
        current_step_key: startGroup,
        last_step_key: null,
        last_error: null,
        started_at: new Date().toISOString(),
      },
    };
    await saveConfig(newConfig);

    // Trigger the first step
    const stepToTrigger = startGroup;
    try {
      await onTriggerStep(stepToTrigger);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err ?? "Unknown trigger error");
      await stopOrchestration(`Error triggering ${startGroup}: ${message}`);
      return;
    }

    toast({
      title: "Batch Auto-Run Started",
      description: `Starting from ${startGroup}...`,
    });
  }, [canRunStepGroup, onTriggerStep, saveConfig, toast]);

  /**
   * Manual run for a step group (respects auto mode for chaining)
   */
  const runStepGroup = useCallback(async (groupKey: StepGroupKey) => {
    if (!canRunStepGroup(groupKey)) {
      const blockedReason = getStepBlockedReason(groupKey) || `${groupKey} dependencies not met`;
      toast({
        title: "Cannot Run",
        description: blockedReason,
        variant: "destructive",
      });
      return;
    }

    const isBatchAutoRunEnabled =
      typeof configRef.current.batch_auto_run_enabled === "boolean"
        ? configRef.current.batch_auto_run_enabled
        : phase1Groups.every((group) => configRef.current.auto_mode[group]);

    // If batch auto-run is enabled, start orchestration from the first manually run step.
    if (isBatchAutoRunEnabled) {
      await startOrchestration(groupKey);
    } else {
      // Just run the step manually
      const stepToTrigger = groupKey;
      try {
        await onTriggerStep(stepToTrigger);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err ?? "Unknown trigger error");
        toast({
          title: "Step Trigger Failed",
          description: `${groupKey}: ${message}`,
          variant: "destructive",
        });
      }
    }
  }, [canRunStepGroup, startOrchestration, onTriggerStep, toast, phase1Groups]);

  return {
    config,
    isSaving,
    orchestratorState,
    toggleAutoMode,
    setPhase1AutoMode,
    getStepGroupStatus,
    canRunStepGroup,
    getStepBlockedReason, // CR175: Expose blocked reason for UI
    startOrchestration,
    stopOrchestration,
    runStepGroup,
    processOrchestrationTick,
  };
}
